/**
 * StatusPill — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/04-components.md § 6
 *
 * Compact status indicator. Five tones. Pill radius, 4px py, 10px px,
 * caption font. Optional leading dot. No emoji inside.
 */

import type { ReactNode } from 'react';

export type StatusTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

interface StatusPillProps {
  tone?: StatusTone;
  /** Show a leading dot in the same color family as the text. */
  withDot?: boolean;
  children: ReactNode;
}

const toneClasses: Record<StatusTone, { bg: string; text: string; dot: string }> = {
  neutral: { bg: 'bg-carnaf-paper-elevated', text: 'text-carnaf-ink-muted', dot: 'bg-carnaf-ink-muted' },
  success: { bg: 'bg-carnaf-teal-soft', text: 'text-carnaf-teal-pressed', dot: 'bg-carnaf-teal-pressed' },
  warning: { bg: 'bg-carnaf-amber-soft', text: 'text-carnaf-amber', dot: 'bg-carnaf-amber' },
  danger: { bg: 'bg-carnaf-red-soft', text: 'text-carnaf-red', dot: 'bg-carnaf-red' },
  info: { bg: 'bg-carnaf-info-soft', text: 'text-carnaf-info', dot: 'bg-carnaf-info' },
};

export function StatusPill({ tone = 'neutral', withDot = false, children }: StatusPillProps): JSX.Element {
  const { bg, text, dot } = toneClasses[tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-caption font-medium ${bg} ${text}`}
    >
      {withDot && <span aria-hidden="true" className={`w-2 h-2 rounded-full ${dot}`} />}
      {children}
    </span>
  );
}
